/**
 * @Author: wuyang
 * @Date: 2024/2/6
 * @Description: ""
 */
import { useEffect, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import {
	AtomManager,
	TListenItemParams
} from '@/helper/managers/atom-manager/AtomManager'
import { useDebounceFn } from 'ahooks'

export const useAtomManagerMethod = <K extends any, T extends any>(
	atomManager: AtomManager<K, T> | undefined
) => {
	const [value, setValue] = useState<TListenItemParams<K, T> | undefined>(
		undefined
	)

	const [dataList, setDataList] = useState<
		{
			itemId: K
			itemData: T
		}[]
	>([])

	const tempDataList = useRef(dataList)

	const debounceSetDataList = useDebounceFn(
		() => {
			setDataList(() => tempDataList.current)
      console.log('调用了节流函数')
		},
		{
			wait: 300
		}
	)

	const changeDataItem = (params: TListenItemParams<K, T>) => {
		if (params.action === 'delItem') {
			tempDataList.current = tempDataList.current.filter(
				(item) => item.itemId !== params.itemId
			)
		} else {
			let isChange = false
			tempDataList.current = tempDataList.current.map((item) => {
				if (item.itemId === params.itemId) {
					isChange = true
					const newItemData = Object.assign({}, item.itemData, params.newValue)
					return {
						itemId: params.itemId,
						itemData: newItemData
					}
				}
				return item
			})
			if (!isChange) {
				tempDataList.current = [
					...tempDataList.current,
					{
						itemId: params.itemId,
						itemData: params.newValue!
					}
				]
			}
		}

		debounceSetDataList.run()
	}

	useEffect(() => {
		const unsub = atomManager?.listenItem((params) => {
			if (!isEqual(params.newValue, params.oldValue)) {
				changeDataItem(params)
				setValue(params)
			}
		})
		return () => {
			tempDataList.current = []
			setDataList([])
			unsub?.()
		}
		// eslint-disable-next-line
	}, [atomManager])

	return [
		value,
		{
			updateItem: atomManager?.updateItem,
			addItem: atomManager?.addItem,
			delItem: atomManager?.delItem
		},
		dataList.map((item) => item.itemData)
	] as [
		TListenItemParams<K, T> | undefined,
		Pick<AtomManager<K, T>, 'updateItem' | 'addItem' | 'delItem'>,
		T[]
	]
}
